export function getHexLuminance(hexColor: string): number | null {
  const normalized = hexColor.trim();
  const match = normalized.match(/^#([0-9a-f]{3}|[0-9a-f]{6})$/i);

  if (!match) {
    return null;
  }

  const hex = match[1].length === 3
    ? match[1].split('').map((char) => char + char).join('')
    : match[1];

  const r = parseInt(hex.slice(0, 2), 16);
  const g = parseInt(hex.slice(2, 4), 16);
  const b = parseInt(hex.slice(4, 6), 16);

  return (0.299 * r + 0.587 * g + 0.114 * b) / 255;
}

export function getContrastRatio(foreground: string, background: string): number | null {
  const foregroundLuminance = getRelativeLuminance(foreground);
  const backgroundLuminance = getRelativeLuminance(background);

  if (foregroundLuminance === null || backgroundLuminance === null) {
    return null;
  }

  const lighter = Math.max(foregroundLuminance, backgroundLuminance);
  const darker = Math.min(foregroundLuminance, backgroundLuminance);

  return (lighter + 0.05) / (darker + 0.05);
}

export function isDarkColor(hexColor: string): boolean {
  const luminance = getHexLuminance(hexColor);
  return luminance === null ? false : luminance < 0.45;
}

export function getReadableTextColor(backgroundColor: string): string {
  return isDarkColor(backgroundColor) ? '#f8fafc' : '#111827';
}

export function getAccessibleTextColor(
  preferredColor: string,
  backgroundColor: string,
  minimumRatio = 4.5,
): string {
  const preferredRatio = getContrastRatio(preferredColor, backgroundColor);

  if (preferredRatio !== null && preferredRatio >= minimumRatio) {
    return preferredColor;
  }

  const light = '#f8fafc';
  const dark = '#111827';
  const lightRatio = getContrastRatio(light, backgroundColor) || 0;
  const darkRatio = getContrastRatio(dark, backgroundColor) || 0;

  return lightRatio >= darkRatio ? light : dark;
}

export function getAccessibleAccentColor(
  preferredColor: string,
  backgroundColor: string,
  fallbackColor?: string,
  minimumRatio = 4.5,
): string {
  const preferredRatio = getContrastRatio(preferredColor, backgroundColor);

  if (preferredRatio !== null && preferredRatio >= minimumRatio) {
    return preferredColor;
  }

  if (fallbackColor) {
    const fallbackRatio = getContrastRatio(fallbackColor, backgroundColor);
    if (fallbackRatio !== null && fallbackRatio >= minimumRatio) {
      return fallbackColor;
    }
  }

  return getAccessibleTextColor(preferredColor, backgroundColor, minimumRatio);
}

export function withHexAlpha(hexColor: string, alphaHex: string): string {
  const normalized = hexColor.trim();
  const match = normalized.match(/^#([0-9a-f]{3}|[0-9a-f]{6})$/i);

  if (!match || !/^[0-9a-f]{2}$/i.test(alphaHex)) {
    return hexColor;
  }

  const hex = match[1].length === 3
    ? match[1].split('').map((char) => char + char).join('')
    : match[1];

  return `#${hex}${alphaHex}`;
}

function getRelativeLuminance(hexColor: string): number | null {
  const normalized = hexColor.trim();
  const match = normalized.match(/^#([0-9a-f]{3}|[0-9a-f]{6})$/i);

  if (!match) {
    return null;
  }

  const hex = match[1].length === 3
    ? match[1].split('').map((char) => char + char).join('')
    : match[1];

  const r = getLinearRgbChannel(parseInt(hex.slice(0, 2), 16));
  const g = getLinearRgbChannel(parseInt(hex.slice(2, 4), 16));
  const b = getLinearRgbChannel(parseInt(hex.slice(4, 6), 16));

  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

function getLinearRgbChannel(value: number): number {
  const channel = value / 255;
  return channel <= 0.03928
    ? channel / 12.92
    : ((channel + 0.055) / 1.055) ** 2.4;
}
